import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  BellIcon, 
  CheckIcon, 
  XMarkIcon,
  GiftIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  TruckIcon,
  CubeIcon
} from '@heroicons/react/24/outline';
import { notificationService } from '../../services/notificationService';
import LogoLoader from "../Common/LogoLoader";
import { useNotificationList } from '../../query/hooks';
import { useAuth } from '../../contexts/AuthContext';

const Notifications = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('all');
  const listQuery = useNotificationList(filter);
  const notifications = listQuery.data?.notifications || [];
  const unreadCount = listQuery.data?.unreadCount || 0;
  const loading = listQuery.isLoading;

  const refreshNotifications = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    if (window.refreshNavbarCounts) window.refreshNavbarCounts();
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      refreshNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      refreshNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await notificationService.delete(notificationId);
      refreshNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'welcome':
        return <UserGroupIcon className="h-5 w-5 text-green-600" />;
      case 'agent_validation':
        return <CheckCircleIcon className="h-5 w-5 text-blue-600" />;
      case 'agent_selected':
        return <UserGroupIcon className="h-5 w-5 text-orange-600" />;
      case 'payment_received':
        return <CurrencyDollarIcon className="h-5 w-5 text-green-600" />;
      case 'reward_available':
        return <GiftIcon className="h-5 w-5 text-purple-600" />;
      case 'reward_delivered':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'referral_approved':
        return <UserGroupIcon className="h-5 w-5 text-orange-600" />;
      case 'sale_completed':
        return <CurrencyDollarIcon className="h-5 w-5 text-green-600" />;
      case 'collection_assigned':
        return <CubeIcon className="h-5 w-5 text-green-700" />;
      case 'chat_message':
        return <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600" />;
      case 'delivery_updated':
        return <TruckIcon className="h-5 w-5 text-green-700" />;
      case 'order_cancelled':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'welcome':
        return 'bg-green-50 border-green-200';
      case 'agent_validation':
        return 'bg-blue-50 border-blue-200';
      case 'agent_selected':
        return 'bg-orange-50 border-orange-200';
      case 'agent_redirected':
        return 'bg-blue-50 border-blue-200';
      case 'payment_received':
        return 'bg-green-50 border-green-200';
      case 'reward_available':
        return 'bg-purple-50 border-purple-200';
      case 'reward_delivered':
        return 'bg-green-50 border-green-200';
      case 'referral_approved':
        return 'bg-orange-50 border-orange-200';
      case 'sale_completed':
      case 'collection_assigned':
      case 'delivery_updated':
        return 'bg-green-50 border-green-200';
      case 'chat_message':
        return 'bg-blue-50 border-blue-200';
      case 'order_cancelled':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 168) {
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification._id);
    }
    
    // Handle navigation based on notification type
    const data = notification.data || {};
    const isAgent = user?.role === "agent";
    switch (notification.type) {
      case 'collection_assigned':
        window.location.href = '/agent/pending-validations';
        break;
      case 'chat_message':
        if (data.binId && isAgent) {
          window.location.href = `/agent/pending-validations?chat=${data.binId}`;
        } else if (data.binId) {
          window.location.href = `/sell?chat=${data.binId}`;
        } else {
          window.location.href = isAgent ? '/agent/pending-validations' : '/sell';
        }
        break;
      case 'delivery_updated':
        window.location.href = '/agent/delivery-history';
        break;
      case 'agent_validation':
      case 'agent_selected':
      case 'order_cancelled':
        window.location.href = '/sell';
        break;
      case 'agent_redirected':
        window.location.href = data.oldAgentName ? '/sell' : '/agent/pending-validations';
        break;
      case 'payment_received':
        window.location.href = data.audience === 'agent' ? '/agent/delivery-history' : '/sales';
        break;
      case 'sale_completed':
        window.location.href = data.sellerName ? '/agent/delivery-history' : '/sales';
        break;
      case 'referral_approved':
        window.location.href = '/referrals';
        break;
      case 'price_update':
        window.location.href = '/materials';
        break;
      case 'bounty_qualified':
      case 'reward_available':
        window.location.href = '/rewards';
        break;
      case 'advert':
      case 'inactivity':
      case 'monthly':
      case 'event':
      case 'announcement':
      case 'welcome':
        window.location.href = '/sell';
        break;
      default:
        break;
    }
  };

  const FilterButton = ({ value, label }) => (
    <button
      onClick={() => setFilter(value)}
      className={`px-4 py-2 text-sm font-medium rounded-md ${
        filter === value
          ? 'bg-green-600 text-white'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
            <p className="text-gray-600">Stay updated with your Jhonks activities</p>
          </div>
          <div className="flex items-center space-x-4">
            {unreadCount > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {unreadCount} unread
              </span>
            )}
            <BellIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Stats */}
      {unreadCount > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-orange-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  You have {unreadCount} unread notifications
                </p>
                <p className="text-sm text-gray-500">
                  Click on notifications to mark them as read
                </p>
              </div>
            </div>
            <button
              onClick={markAllAsRead}
              className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700"
            >
              Mark All as Read
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Filter:</span>
          <FilterButton value="all" label="All" />
          <FilterButton value="unread" label="Unread" />
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Your Notifications</h2>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <LogoLoader />
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <BellIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No notifications yet</p>
            <p className="text-sm">You'll see important updates here</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {notifications.map((notification) => (
              <div
                key={notification._id}
                className={`p-6 hover:bg-gray-50 cursor-pointer transition-colors ${
                  !notification.read ? 'bg-blue-50' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className={`flex-shrink-0 p-2 rounded-full ${getNotificationColor(notification.type)}`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className={`text-sm font-medium ${
                          !notification.read ? 'text-gray-900 font-semibold' : 'text-gray-700'
                        }`}>
                          {notification.title}
                        </h3>
                        <div className="flex items-center space-x-2">
                          {!notification.read && (
                            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                          )}
                          <span className="text-xs text-gray-500">
                            {formatDate(notification.createdAt)}
                          </span>
                        </div>
                      </div>
                      <p className="mt-1 text-sm text-gray-600">
                        {notification.message}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!notification.read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification._id);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Mark as read
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification._id);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
